import React, { useCallback, useEffect, useState } from "react";
import { CheckCircle2, XCircle, Loader2, Mail, Phone, MapPin, Calendar, Clock, Compass } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "@/shared/lib/api";

/**
 * AdminWaitlist — operator-only queue for the controlled-access rollout.
 *
 * Lists waitlist applications (default: status=WAITLIST) with the full
 * demand-mapping payload: name, contact, home + destination, days,
 * pickup/return windows, frequency, current method. Approve/Reject
 * actions hit the existing /api/admin/waitlist/:user_id/{approve,reject}
 * endpoints. On approve the rider's user.access_status flips to APPROVED
 * and they immediately unlock booking.
 */
const STATUS_FILTERS = [
  { key: "WAITLIST", label: "Pending" },
  { key: "APPROVED", label: "Approved" },
  { key: "REJECTED", label: "Rejected" },
  { key: "ALL",      label: "All" },
];

export default function AdminWaitlist() {
  const [status, setStatus] = useState("WAITLIST");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/waitlist", { params: { status } });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not load waitlist");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const decide = async (row, action) => {
    setBusyId(row.user_id);
    try {
      await api.post(`/admin/waitlist/${row.user_id}/${action}`, { note: "" });
      toast.success(action === "approve" ? `${row.first_name} approved` : `${row.first_name} rejected`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not update application");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <div className="max-w-5xl mx-auto px-3 sm:px-6 py-6" data-testid="admin-waitlist-page">
        <div className="flex items-end justify-between gap-4 mb-5">
          <div>
            <div className="text-[10px] tracking-[0.26em] uppercase font-medium text-[#ed6500] mb-1">Controlled access</div>
            <h1 className="font-serif text-3xl text-[#0A1128] leading-tight">Rider waitlist</h1>
            <p className="text-[13px] text-[#515E7B] mt-1">
              Approve riders once you can serve their area. Approval unlocks booking immediately.
            </p>
          </div>
        </div>

        {/* Status filter tabs */}
        <div className="inline-flex bg-white border hairline rounded-full p-1 mb-5" data-testid="admin-waitlist-filters">
          {STATUS_FILTERS.map((f) => {
            const active = status === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setStatus(f.key)}
                className={`h-9 px-4 rounded-full text-[10px] tracking-[0.22em] uppercase font-semibold transition-colors ${
                  active ? "bg-[#0A1128] text-white" : "text-[#515E7B] hover:text-[#0A1128]"
                }`}
                data-testid={`admin-waitlist-filter-${f.key.toLowerCase()}`}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 size={20} className="animate-spin text-[#515E7B]" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 bg-white border hairline rounded-2xl" data-testid="admin-waitlist-empty">
            <div className="font-serif text-xl text-[#0A1128]">Nothing here</div>
            <p className="text-[13px] text-[#515E7B] mt-1">No applications match this filter.</p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="admin-waitlist-list">
            {rows.map((row) => (
              <li
                key={row.user_id}
                className="bg-white border hairline rounded-2xl p-5"
                data-testid={`admin-waitlist-row-${row.user_id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-serif text-xl text-[#0A1128] leading-tight">
                      {row.first_name} {row.last_name}
                    </div>
                    <div className="text-[12px] text-[#515E7B] mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="inline-flex items-center gap-1"><Mail size={11} /> {row.user_email || row.email}</span>
                      <span className="inline-flex items-center gap-1"><Phone size={11} /> {row.phone || "—"}</span>
                    </div>
                  </div>
                  <StatusPill status={row.status} />
                </div>

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-[13px]">
                  <Cell icon={MapPin} label="Home">{row.home_address || "—"}</Cell>
                  <Cell icon={MapPin} label="Destination">{row.destination_address || "—"}</Cell>
                  <Cell icon={Calendar} label="Days">{(row.days_needed || []).join(" · ") || "—"}</Cell>
                  <Cell icon={Clock} label="Pickup window">{formatWindow(row.pickup_window)}</Cell>
                  <Cell icon={Clock} label="Return window">{formatWindow(row.return_window)}</Cell>
                  <Cell icon={Compass} label="Frequency">{row.frequency || "—"}</Cell>
                </div>

                {row.current_method && (
                  <div className="mt-3 text-[12px] text-[#515E7B]">
                    Today: <span className="text-[#0A1128]">{row.current_method}</span>
                  </div>
                )}

                {row.status === "WAITLIST" && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      onClick={() => decide(row, "approve")}
                      disabled={busyId === row.user_id}
                      className="inline-flex items-center gap-2 bg-[#ed6500] hover:bg-[#ff7b1f] text-white px-4 py-2.5 rounded-xl text-[11px] tracking-[0.22em] uppercase font-semibold disabled:opacity-50"
                      data-testid={`admin-waitlist-approve-${row.user_id}`}
                    >
                      {busyId === row.user_id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                      Approve rider
                    </button>
                    <button
                      onClick={() => decide(row, "reject")}
                      disabled={busyId === row.user_id}
                      className="inline-flex items-center gap-2 bg-white border hairline text-[#515E7B] hover:text-[#ed6500] hover:border-[#ed6500] px-4 py-2.5 rounded-xl text-[11px] tracking-[0.22em] uppercase font-semibold disabled:opacity-50"
                      data-testid={`admin-waitlist-reject-${row.user_id}`}
                    >
                      <XCircle size={12} />
                      Reject
                    </button>
                  </div>
                )}
                {row.admin_note && (
                  <div className="mt-3 text-[11.5px] text-[#515E7B] italic">Note: {row.admin_note}</div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

function Cell({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={13} strokeWidth={1.8} className="text-[#ed6500] mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] tracking-[0.2em] uppercase font-medium text-[#515E7B] mb-0.5">{label}</div>
        <div className="text-[#0A1128] break-words">{children}</div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const styles = {
    WAITLIST: "bg-[#FCEADE] text-[#ed6500]",
    APPROVED: "bg-emerald-100 text-emerald-700",
    REJECTED: "bg-rose-100 text-rose-700",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[9px] tracking-[0.22em] uppercase font-semibold ${styles[status] || "bg-gray-100 text-gray-600"}`}>
      {status}
    </span>
  );
}

function formatWindow(w) {
  if (!w) return "—";
  const [start, end] = w.split("-");
  if (!start || !end) return w;
  const fmt = (s) => {
    const [hh, mm] = s.split(":").map((n) => parseInt(n, 10));
    const ampm = hh >= 12 ? "PM" : "AM";
    const h12 = ((hh + 11) % 12) + 1;
    return `${h12}:${String(mm).padStart(2, "0")} ${ampm}`;
  };
  return `${fmt(start)} – ${fmt(end)}`;
}
